import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  buildCalendarCells,
  computeDueDatePickerFixedStyle,
  formatIsoDate,
  parseIsoDateParts,
} from "../../../core/components/calendarUtils";

// Re-exported for existing importers of this module -- the implementations
// now live in core/components/calendarUtils.js (Phase 5a), since they are
// generic date/calendar math with no action-item-specific logic.
export { buildCalendarCells, computeDueDatePickerFixedStyle, formatIsoDate, parseIsoDateParts };

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Inline due-date calendar; only commits when the user clicks a day (month arrows change view only).
 *
 * @example
 * <ActionItemDueDatePicker
 *   value="2026-05-26"
 *   onSelect={(iso) => saveDue(iso)}
 *   onClear={() => saveDue("")}
 *   onClose={() => setOpen(false)}
 * />
 */
const ActionItemDueDatePicker = ({
  value,
  onSelect,
  onClear,
  onClose,
  anchorRef = null,
  placement = "auto",
  align = "start",
}) => {
  const rootRef = useRef(null);
  const [fixedStyle, setFixedStyle] = useState(null);
  const usePortal = Boolean(anchorRef);
  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);
  const todayIso = formatIsoDate(today.getFullYear(), today.getMonth(), today.getDate());
  const selectedIso = String(value || "").trim().slice(0, 10);
  const initialParts = parseIsoDateParts(selectedIso) || {
    year: today.getFullYear(),
    monthIndex: today.getMonth(),
    day: today.getDate(),
  };

  const [viewYear, setViewYear] = useState(initialParts.year);
  const [viewMonthIndex, setViewMonthIndex] = useState(initialParts.monthIndex);

  const updateFixedPosition = useCallback(() => {
    if (!usePortal || !anchorRef?.current) return;
    const nextStyle = computeDueDatePickerFixedStyle(anchorRef.current, rootRef.current, {
      placement,
      align,
    });
    setFixedStyle(nextStyle);
  }, [align, anchorRef, placement, usePortal]);

  useLayoutEffect(() => {
    if (!usePortal) return undefined;
    updateFixedPosition();
    const pickerEl = rootRef.current;
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && pickerEl
        ? new ResizeObserver(() => updateFixedPosition())
        : null;
    resizeObserver?.observe(pickerEl);
    window.addEventListener("resize", updateFixedPosition);
    window.addEventListener("scroll", updateFixedPosition, true);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateFixedPosition);
      window.removeEventListener("scroll", updateFixedPosition, true);
    };
  }, [updateFixedPosition, usePortal, viewYear, viewMonthIndex]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const target = event.target;
      if (rootRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [anchorRef, onClose]);

  const cells = useMemo(
    () => buildCalendarCells(viewYear, viewMonthIndex),
    [viewYear, viewMonthIndex]
  );

  const shiftMonth = (delta) => {
    const anchor = new Date(viewYear, viewMonthIndex + delta, 1);
    setViewYear(anchor.getFullYear());
    setViewMonthIndex(anchor.getMonth());
  };

  const pickerNode = (
    <div
      ref={rootRef}
      className={[
        "action-item-due-picker",
        usePortal ? "action-item-due-picker--portaled" : "",
        fixedStyle?.openAbove ? "action-item-due-picker--above" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="dialog"
      aria-label="Choose due date"
      style={
        usePortal
          ? {
              position: "fixed",
              top: fixedStyle?.top ?? -9999,
              left: fixedStyle?.left ?? -9999,
              visibility: fixedStyle ? "visible" : "hidden",
              zIndex: 10000,
            }
          : undefined
      }
      onClick={(e) => e.stopPropagation()}
    >
      <div className="action-item-due-picker__nav">
        <button
          type="button"
          className="action-item-due-picker__nav-btn"
          onClick={() => shiftMonth(-1)}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="action-item-due-picker__month-label">
          {MONTH_NAMES[viewMonthIndex]} {viewYear}
        </span>
        <button
          type="button"
          className="action-item-due-picker__nav-btn"
          onClick={() => shiftMonth(1)}
          aria-label="Next month"
        >
          ›
        </button>
      </div>
      <div className="action-item-due-picker__weekdays" aria-hidden="true">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="action-item-due-picker__weekday">
            {label}
          </span>
        ))}
      </div>
      <div className="action-item-due-picker__grid">
        {cells.map((cell, index) => {
          const iso = formatIsoDate(cell.year, cell.monthIndex, cell.day);
          const isSelected = iso === selectedIso;
          const isToday = iso === todayIso;
          const classNames = [
            "action-item-due-picker__day",
            cell.inMonth ? "" : "action-item-due-picker__day--outside",
            isSelected ? "action-item-due-picker__day--selected" : "",
            isToday ? "action-item-due-picker__day--today" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <button
              key={`${iso}-${index}`}
              type="button"
              className={classNames}
              onClick={() => onSelect(iso)}
              aria-label={iso}
              aria-pressed={isSelected}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
      {onClear && selectedIso ? (
        <button
          type="button"
          className="action-item-due-picker__clear"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onClear();
            onClose();
          }}
        >
          Clear due date
        </button>
      ) : null}
    </div>
  );

  return usePortal ? createPortal(pickerNode, document.body) : pickerNode;
};

export default ActionItemDueDatePicker;
